import React, { useCallback, useMemo, useRef } from 'react'
import { Dropdown, Tooltip } from 'antd'
import { CloseOutlined, SearchOutlined } from '@ant-design/icons'
import TitleAutocomplete from './TitleAutocomplete'
import {
  setActiveAutocomplete,
  resetDynamicLabelToInitial,
  setAutocompleteValues,
  saveFilters,
  removeFilter,
} from '../../../Slices/Kiosk/TableOptionsSlice'
import { populateTableWithArticles } from '../../../Services/Kiosk/ArticleServices'
import { renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { formatString, setCapitalLetter } from '../../../Helpers/StringHelpers'
import { mktFilterYes, mktFilterAll, mktFilterNo, TableColumnTranslationPrefix } from '../../../Constants/Configuration'
import {
  ArticleColumn,
  initalHeaderMode,
  autocompleteHeaderMode,
  DefaultCols,
} from '../../../Constants/TableConfiguration'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { addHoverClassToTag, removeHoverClassFromTag } from 'Helpers/FilterHelper'

function TitleDynamicLabel(props) {
  const dispatch = useAppDispatch()
  const activeAutocomplete = useAppSelector(state => state.present.tableOptions.activeAutocomplete)
  const language = useAppSelector(state => state.present.language.value)
  const tableFilters = useAppSelector(state => state.present.tableOptions.tableFilters)
  const emptyTableColumns = useAppSelector(state => state.present.tableOptions.emptyTableColumns)
  const emptyExtraDescriptions = useAppSelector(state => state.present.tableOptions.emptyExtraDescriptions)

  const hasMultipleSearch = DefaultCols.find(x => x.name === props.label)?.multipleSearch

  const isMarketplace = useMemo(() => {
    return props.isMarketplace
  }, [props.isMarketplace])
  const mktColumn = useMemo(() => {
    return props.mktColumn
  }, [props.mktColumn])
  const filter = useMemo(() => {
    return isMarketplace ? tableFilters[mktColumn] : tableFilters[props.label]
  }, [isMarketplace, mktColumn, props.label, tableFilters])

  const specialLanguageColumnFilter = useMemo(() => {
    return (
      props.language &&
      emptyExtraDescriptions.filter(desc => {
        return desc.languageCode === props.language && desc.field === props.label
      }).length > 0
    )
  }, [emptyExtraDescriptions, props.label, props.language])

  const emptyColumnKeys = useMemo(() => {
    return Object.keys(emptyTableColumns)
  }, [emptyTableColumns])

  const isColumnEmpty = useMemo(() => {
    let isEmpty = false

    emptyColumnKeys.forEach(group => {
      if (emptyTableColumns[group].filter(element => element.value === props.label && element.active).length !== 0) {
        isEmpty = true
      }
    })

    return isEmpty
  }, [emptyColumnKeys, emptyTableColumns, props.label])

  const filterLabelToSet = useMemo(() => {
    return isMarketplace
      ? filter && filter === mktFilterYes
        ? props.label + ' ' + renderTranslation(language, TranslationKeys.tableMktYes)
        : props.label + ' ' + renderTranslation(language, TranslationKeys.tableMktNo)
      : specialLanguageColumnFilter
      ? formatString(
          renderTranslation(language, TranslationKeys.tableEmptyColumnsAutocomplete),
          renderTranslation(language, TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.label)]) +
            ' ' +
            props.language,
        )
      : filter && isColumnEmpty
      ? formatString(
          renderTranslation(language, TranslationKeys.tableEmptyColumnsAutocomplete),
          renderTranslation(language, TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.label)]),
        )
      : filter
  }, [filter, isColumnEmpty, isMarketplace, language, props.label, props.language, specialLanguageColumnFilter])

  const headerMode = useMemo(() => {
    return !filter && activeAutocomplete !== props.label
      ? initalHeaderMode
      : activeAutocomplete === props.label
      ? autocompleteHeaderMode
      : ''
  }, [filter, props.label, activeAutocomplete])

  const switchToInitial = useCallback(
    e => {
      e.stopPropagation()
      const objectToDispatch = {
        label: props.label,
        columnEmpty: isColumnEmpty,
        specialLanguageFilter: specialLanguageColumnFilter,
        languageCode: props.language,
      }

      dispatch(resetDynamicLabelToInitial(objectToDispatch))
      dispatch(setAutocompleteValues({ label: props.label, text: '' }))
      inputElement.current.parentNode.parentNode.parentNode.removeAttribute('draggable')

      dispatch(populateTableWithArticles())
    },
    [dispatch, isColumnEmpty, specialLanguageColumnFilter, props.label, props.language],
  )

  const resetMultipleSearchInput = useCallback(() => {
    if (hasMultipleSearch) {
      removeHoverClassFromTag(props.label)
      dispatch(setAutocompleteValues({ label: props.label, text: '' }))
    }
  }, [dispatch, hasMultipleSearch, props.label])

  const switchToAutocomplete = useCallback(
    e => {
      e.stopPropagation()
      if (props.disableSearch) return

      dispatch(setActiveAutocomplete(props.label))
      inputElement.current.parentNode.parentNode.parentNode.draggable = false
    },
    [dispatch, props.disableSearch, props.label],
  )

  const reSwitchToAutocomplete = useCallback(
    e => {
      e.stopPropagation()
      if (props.disableSearch) return

      if (isColumnEmpty) return

      resetMultipleSearchInput()
      dispatch(setActiveAutocomplete(props.label))
      inputElement.current.parentNode.parentNode.parentNode.draggable = false
    },
    [dispatch, isColumnEmpty, props.disableSearch, props.label, resetMultipleSearchInput],
  )

  const handleClick = useCallback(
    e => {
      if (activeAutocomplete !== '') {
        dispatch(setActiveAutocomplete(''))
        document.querySelectorAll('th[draggable]').forEach(el => {
          el.removeAttribute('draggable')
        })
      }
    },
    [activeAutocomplete, dispatch],
  )

  const setAutocompleteWindowListener = useCallback(() => {
    if (!filter) {
      if (headerMode === autocompleteHeaderMode) {
        window.addEventListener('click', handleClick)
      } else {
        window.removeEventListener('click', handleClick)
      }
    }
    return () => {
      window.removeEventListener('click', handleClick)
    }
  }, [filter, handleClick, headerMode])

  setAutocompleteWindowListener()

  const onFocusChange = useCallback(() => {
    dispatch(setActiveAutocomplete(props.label))
    inputElement.current.parentNode.parentNode.parentNode.draggable = false
  }, [dispatch, props.label])

  const removeAutocomplete = () => {
    dispatch(setActiveAutocomplete(''))
    inputElement.current.parentNode.parentNode.parentNode.removeAttribute('draggable')
  }

  const onMktValueSelect = useCallback(
    key => {
      if (key !== mktFilterAll) {
        dispatch(saveFilters({ key: mktColumn, value: key }))
      } else {
        dispatch(removeFilter(mktColumn))
      }
      dispatch(populateTableWithArticles())
    },
    [dispatch, mktColumn],
  )

  const menu = useMemo(() => {
    return isMarketplace
      ? {
          items: [
            {
              label: (
                <div
                  onClick={() => {
                    onMktValueSelect(mktFilterAll)
                  }}
                >
                  {renderTranslation(language, TranslationKeys.tableDotsMenuMktAll)}
                </div>
              ),
              key: mktFilterAll,
            },
            {
              label: (
                <div
                  onClick={() => {
                    onMktValueSelect(mktFilterYes)
                  }}
                >
                  {renderTranslation(language, TranslationKeys.tableDotsMenuMktYes)}
                </div>
              ),
              key: mktFilterYes,
            },
            {
              label: (
                <div
                  onClick={() => {
                    onMktValueSelect(mktFilterNo)
                  }}
                >
                  {renderTranslation(language, TranslationKeys.tableDotsMenuMktNo)}
                </div>
              ),
              key: mktFilterNo,
            },
          ],
          style: { minWidth: '100px' },
        }
      : {}
  }, [isMarketplace, language, onMktValueSelect])

  const inputElement = useRef(null)

  const renderTitleAutoComplete = () => {
    return (
      <TitleAutocomplete
        style={props.style}
        title={renderTranslation(
          language,
          TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.label)],
        )}
        label={props.label}
        onSelect={removeAutocomplete}
        onClick={stopPropagation}
        isFromDiWa={true}
      />
    )
  }

  return (
    <React.Fragment>
      <div ref={inputElement}></div>
      {isMarketplace ? (
        filter ? (
          <div style={props.style} className='complete-search-label truncate-text'>
            <span
              className='clear-search-icon'
              onClick={() => {
                onMktValueSelect(mktFilterAll)
              }}
            >
              <CloseOutlined className='general-clear-icon cursor-pointer' />
            </span>
            <Dropdown menu={menu} trigger={['click']}>
              <span className={'cursor-pointer'}>{filterLabelToSet}</span>
            </Dropdown>
          </div>
        ) : (
          <Dropdown menu={menu} trigger={['click']}>
            <div style={props.style} className={'custom-article-title cursor-pointer'}>
              {props.label}
              <SearchOutlined style={{ paddingLeft: 8 }} />
            </div>
          </Dropdown>
        )
      ) : filter || specialLanguageColumnFilter ? (
        headerMode !== autocompleteHeaderMode ? (
          <Tooltip title={filter && isColumnEmpty ? filterLabelToSet : undefined}>
            <div
              style={props.style}
              className={
                'complete-search-label truncate-text' +
                (specialLanguageColumnFilter ? ' special-language-truncate' : '') +
                (props.label === ArticleColumn.Size ? ' size-search-label' : '')
              }
            >
              <span className='clear-search-icon' onClick={switchToInitial}>
                <CloseOutlined className='general-clear-icon cursor-pointer' />
              </span>
              <span
                className={
                  isColumnEmpty || specialLanguageColumnFilter ? 'search-empty cursor-pointer' : 'cursor-pointer'
                }
                onClick={reSwitchToAutocomplete}
                tabIndex={0}
                onMouseEnter={e => {
                  addHoverClassToTag(props.label)
                }}
                onMouseLeave={e => {
                  removeHoverClassFromTag(props.label)
                }}
              >
                {hasMultipleSearch && filter.split('|').length > 1 && !isColumnEmpty
                  ? filter.split('|').length +
                    ' ' +
                    renderTranslation(language, TranslationKeys.multipleSearchResultsLabel)
                  : filterLabelToSet}
                {hasMultipleSearch && !isColumnEmpty ? <span className='ml-1'>+</span> : null}
              </span>
            </div>
          </Tooltip>
        ) : (
          renderTitleAutoComplete()
        )
      ) : headerMode === autocompleteHeaderMode ? (
        renderTitleAutoComplete()
      ) : (
        <div
          style={props.style}
          className={'custom-article-title' + (props.label === ArticleColumn.Size ? ' size-title' : '')}
          onClick={switchToAutocomplete}
        >
          {renderTranslation(language, TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(props.label)])}{' '}
          {props.language}{' '}
          {!props.disableSearch && (
            <SearchOutlined
              style={props.label === ArticleColumn.Size ? { paddingLeft: 5 } : { paddingLeft: 8 }}
              tabIndex={0}
              onFocus={onFocusChange}
            />
          )}
        </div>
      )}
    </React.Fragment>
  )
}

export default TitleDynamicLabel
